export type PropertyType = 'apartment' | 'house' | 'room';

export interface ReportParams {
  postnummer: string;
  address?: string;
  propertyType?: PropertyType;
  areaM2Min?: number;
  areaM2Max?: number;
  roomsMin?: number;
  roomsMax?: number;
}

export interface ReportSummary {
  listingCount: number;
  medianRent: number;
  medianKrPerSqm: number;
  p25Rent: number;
  p75Rent: number;
  p25KrPerSqm: number;
  p75KrPerSqm: number;
}

export interface ReportComparable {
  id: string;
  address: string;
  postnummer: string;
  m2: number;
  rooms: number;
  rentKrPerMonth: number;
  krPerSqm: number;
  source: string;
  listedAt: string;
}

export interface ReportResult {
  locationLabel: string;
  generatedAt: string;
  summary: ReportSummary;
  comparables: ReportComparable[];
}

// --- Internal camelCase ↔ snake_case mapping ---

interface RawPayload {
  postnummer?: string;
  address?: string;
  property_type?: PropertyType;
  area_m2_min?: number;
  area_m2_max?: number;
  rooms_min?: number;
  rooms_max?: number;
}

function paramsToPayload(p: ReportParams): RawPayload {
  const out: RawPayload = {};
  if (p.postnummer) out.postnummer = p.postnummer;
  if (p.address) out.address = p.address;
  if (p.propertyType) out.property_type = p.propertyType;
  if (p.areaM2Min != null) out.area_m2_min = p.areaM2Min;
  if (p.areaM2Max != null) out.area_m2_max = p.areaM2Max;
  if (p.roomsMin != null) out.rooms_min = p.roomsMin;
  if (p.roomsMax != null) out.rooms_max = p.roomsMax;
  return out;
}

interface RawResult {
  location_label: string;
  generated_at: string;
  summary: {
    listing_count: number;
    median_rent: number;
    median_kr_per_sqm: number;
    p25_rent: number;
    p75_rent: number;
    p25_kr_per_sqm: number;
    p75_kr_per_sqm: number;
  };
  comparables: Array<{
    id: string;
    address: string;
    postnummer: string;
    m2: number;
    rooms: number;
    rent_kr_per_month: number;
    kr_per_sqm: number;
    source: string;
    listed_at: string;
  }>;
}

function fromRaw(raw: RawResult): ReportResult {
  return {
    locationLabel: raw.location_label,
    generatedAt: raw.generated_at,
    summary: {
      listingCount: raw.summary.listing_count,
      medianRent: raw.summary.median_rent,
      medianKrPerSqm: raw.summary.median_kr_per_sqm,
      p25Rent: raw.summary.p25_rent,
      p75Rent: raw.summary.p75_rent,
      p25KrPerSqm: raw.summary.p25_kr_per_sqm,
      p75KrPerSqm: raw.summary.p75_kr_per_sqm,
    },
    comparables: raw.comparables.map((c) => ({
      id: c.id,
      address: c.address,
      postnummer: c.postnummer,
      m2: c.m2,
      rooms: c.rooms,
      rentKrPerMonth: c.rent_kr_per_month,
      krPerSqm: c.kr_per_sqm,
      source: c.source,
      listedAt: c.listed_at,
    })),
  };
}

export async function fetchRentalAssessment(params: ReportParams): Promise<ReportResult> {
  const res = await fetch('/api/reports/rental-assessment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(paramsToPayload(params)),
  });
  if (!res.ok) throw new Error(`Report API responded with ${res.status}`);
  return fromRaw((await res.json()) as RawResult);
}

export function downloadRentalAssessmentPdf(params: ReportParams): void {
  void fetch('/api/reports/rental-assessment/pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(paramsToPayload(params)),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`PDF endpoint responded with ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rental-assessment.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}
